/**
 * The section types a marketing page can be built from - `SAAS.md` §3.45.
 *
 * **One list, because it used to be two.** The Tower's editor held `KNOWN_SECTIONS` and the public
 * site held `RENDERABLE`: nine entries each, same order, no relationship, and each comment describing
 * itself as *the types the site can render*. That is one claim made twice in two applications that
 * are deployed separately.
 *
 * **And the failure mode is silent in the direction that matters.** A type the editor offers and the
 * renderer does not know gets published, and the public site drops it - `App.tsx` draws the
 * renderable sections and records the rest in a `hidden` attribute for tests. The editor shows the
 * section as published; the visitor never sees it; nothing says so. The other direction merely hides
 * a feature somebody built.
 *
 * It was **latent** when this was written - the owner's `home` carries nine sections, one of each of
 * the nine - so this is what keeps it that way rather than a repair. `SectionView`'s `switch` is the
 * other half of the rule and `marketing-sections.test.ts` pins the two together.
 *
 * **The order is part of the contract**: the editor lists types in this order in its picker, so a
 * reordering here reorders a screen. Add at the end.
 *
 * **And nine is this build's number, not legacy's - corrected 2026-09-20, §3.66.** The paragraph
 * above says *nine entries each*, which was measured across AGOS's two lists and never against the
 * source: legacy's `marketing-site/src/sections.tsx` has a `REGISTRY` of **eighteen**, and its editor
 * has a form for each. The nine here are the nine the owner's `home` uses; `banner`, `stats`,
 * `split`, `steps`, `quote`, `video`, `spacer`, `image` and `html` are capabilities nobody's data
 * exercises, and `html` is a raw-HTML sink that wants a decision before a renderer. Not a gap being
 * hidden - a gap with a number on it.
 *
 * **Two of those nine are built - §3.129, and the count here is now eleven.** `stats` and `steps` were the
 * two whose port needed no decision: both carry an `items` list the readers already know, so nothing about
 * how an existing section is read changed. The remaining seven each have a reason recorded in §3.129 rather
 * than a place in a queue - `html` and `video` want a decision about operator-controlled markup, `split`
 * wants one about where a section's paragraph is read from, and four were presentation.
 *
 * **Those four shipped as §3.130, so this list is fifteen and legacy's eighteen is three away.** What is left
 * is `video` and `html`, both owner question #71 - `split` shipped as §3.131, which makes this **sixteen**.
 */
export const MARKETING_SECTION_TYPES = [
  'hero',
  'features',
  'logos',
  'pricing',
  'gallery',
  'testimonials',
  'faq',
  'cta',
  'footer',
  /* §3.129 - two of the nine §3.66 counted. **At the end because the order is the editor's picker order**, so
     inserting them beside their cousins would reorder a screen for no reason. */
  'stats',
  'steps',
  /* §3.130 - the four that needed no decision. `split` wants a judgement about where a section's paragraph is
     read from, and `video` and `html` are owner question #71, so fifteen is where this stops until one of those
     is answered. */
  'banner',
  'quote',
  'image',
  'spacer',
  /* §3.131 - the judgement §3.129 set aside: its paragraph is read by its own case from `body`, not by the
     shared chrome from `subtitle`, so nothing about the other fifteen types moved. */
  'split',
  /* §3.178 - the seventeenth, and owner question #71's own recommendation rather than a guess: a host allow-list
     of YouTube and Vimeo. What waited on the owner was **which extra services**, which is one entry in
     `VIDEO_HOSTS` rather than a decision. `html` is the eighteenth and stays unported, because #71's option (b)
     turns on who else holds a Tower login. */
  'video',
] as const

export type MarketingSectionType = (typeof MARKETING_SECTION_TYPES)[number]

/** Whether the public site can draw a section of this type. */
export function isMarketingSectionType(type: string): boolean {
  return (MARKETING_SECTION_TYPES as readonly string[]).includes(type)
}
